import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  type CommentableEntity,
  type CreateCommentParams,
  createComment as createCommentRequest,
  deleteComment as deleteCommentRequest,
  toggleHideReplies as toggleHideRepliesRequest,
  togglePinComment as togglePinCommentRequest,
  type UpdateCommentParams,
  updateComment as updateCommentRequest,
} from "../services/comment.api"

interface UseCommentMutationsOptions {
  entityType: CommentableEntity
  entityId: string
}

export function useCommentMutations({ entityType, entityId }: UseCommentMutationsOptions) {
  const queryClient = useQueryClient()
  const invalidateComments = () =>
    queryClient.invalidateQueries({
      queryKey: ["comments", entityType, entityId],
    })
  const invalidateCommunityStatistics = () => {
    if (entityType !== "community") {
      return Promise.resolve()
    }

    return queryClient.invalidateQueries({
      queryKey: ["community-statistics", entityId],
    })
  }
  const invalidateRelatedQueries = async () => {
    await invalidateComments()
    await invalidateCommunityStatistics()
  }

  const createComment = useMutation({
    mutationFn: (params: CreateCommentParams) => createCommentRequest(entityType, entityId, params),
    onSuccess: invalidateRelatedQueries,
  })

  const updateComment = useMutation({
    mutationFn: ({ commentId, params }: { commentId: string; params: UpdateCommentParams }) =>
      updateCommentRequest(commentId, params),
    onSuccess: invalidateRelatedQueries,
  })

  const deleteComment = useMutation({
    mutationFn: (commentId: string) => deleteCommentRequest(commentId),
    onSuccess: invalidateRelatedQueries,
  })

  const togglePinComment = useMutation({
    mutationFn: (commentId: string) => togglePinCommentRequest(commentId),
    onSuccess: invalidateRelatedQueries,
  })

  const toggleHideReplies = useMutation({
    mutationFn: (commentId: string) => toggleHideRepliesRequest(commentId),
    onSuccess: invalidateRelatedQueries,
  })

  return {
    createComment: createComment.mutateAsync,
    updateComment: updateComment.mutateAsync,
    deleteComment: deleteComment.mutateAsync,
    togglePinComment: togglePinComment.mutateAsync,
    toggleHideReplies: toggleHideReplies.mutateAsync,
    isCreating: createComment.isPending,
    isUpdating: updateComment.isPending,
    isDeleting: deleteComment.isPending,
    isTogglingPin: togglePinComment.isPending,
    isTogglingHideReplies: toggleHideReplies.isPending,
  }
}
